import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, ScrollView, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { SkeletonRow } from "@/components/ui/Skeleton";
import { SSNInput } from "@/components/ui/SSNInput";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { UserType } from "@/types/db/users";
import { useTheme } from "@/hooks/useTheme";
import usePermissions from "@/hooks/usePermissions";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { userService } from "@/services/userService";
import useToast from "@/hooks/useToast";
import { Button } from "@/components/ui/Button";

export function UserPrivateTab() {
  const { colors } = useTheme();
  const { entityData, loading } = useEntityEdit<UserType>();
  const { user_private: privatePermissions } = usePermissions();
  const toast = useToast();

  // Local state - independent of parent edit state
  const [localEditing, setLocalEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ssn, setSsn] = useState("");
  const [originalSsn, setOriginalSsn] = useState("");

  // Derive canEdit from permissions
  const canEdit = privatePermissions.create || privatePermissions.update;

  // Check if SSN is valid (empty or matches XXX-XX-XXXX format)
  const isValidSsn = ssn.length === 0 || /^\d{3}-\d{2}-\d{4}$/.test(ssn);
  const isDirty = ssn !== originalSsn;
  const canSave = isDirty && isValidSsn;

  // Sync local state with entity data
  useEffect(() => {
    const privateData = entityData?.user_private?.[0];
    const ssnValue = privateData?.ssn || "";
    setSsn(ssnValue);
    setOriginalSsn(ssnValue);
  }, [entityData?.user_private]);

  const handleSave = useCallback(async () => {
    if (!entityData?.id || !isDirty || !isValidSsn) return;

    setSaving(true);
    try {
      await userService.updateUserPrivate(entityData.id, { ssn });
      setOriginalSsn(ssn); // Update original to current after successful save
      toast.showToast({ title: "Saved", message: "Private information updated", toastType: "success" });
      setLocalEditing(false);
    } catch (error: any) {
      toast.showToast({ title: "Error", message: error.message, toastType: "error" });
    } finally {
      setSaving(false);
    }
  }, [entityData?.id, ssn, isDirty, isValidSsn, toast]);

  const handleCancel = useCallback(() => {
    // Revert to original data
    const privateData = entityData?.user_private?.[0];
    setSsn(privateData?.ssn || "");
    setLocalEditing(false);
  }, [entityData?.user_private]);

  const handleDelete = useCallback(async () => {
    if (!entityData?.id) return;

    setSaving(true);
    try {
      await userService.updateUserPrivate(entityData.id, { ssn: "" });
      setSsn("");
      toast.showToast({ title: "Deleted", message: "SSN removed", toastType: "success" });
      setLocalEditing(false);
    } catch (error: any) {
      toast.showToast({ title: "Error", message: error.message, toastType: "error" });
    } finally {
      setSaving(false);
    }
  }, [entityData?.id, toast]);

  // If no read permission, show access denied
  if (!privatePermissions.read) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
        <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
          Access Denied
        </ThemedText>
        <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>
          You don&apos;t have permission to view private information
        </ThemedText>
      </ScrollView>
    );
  }

  if (loading || !entityData) {
    return (
      <View style={styles.container}>
        <View style={styles.section}>
          <SkeletonRow />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} pointerEvents={saving ? "none" : "auto"}>
      <ScrollView style={styles.scrollContent} contentContainerStyle={styles.scrollContainer}>
        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
          {/* Card Header with Edit/Save Actions */}
          <View style={[styles.cardHeader, { borderBottomColor: colors.border }]}>
            <ThemedText type="defaultSemiBold">Private Information</ThemedText>
            <View style={styles.headerActions}>
              {localEditing ? (
                <>
                  <Button text="Cancel" onPress={handleCancel} style={styles.headerButton} disabled={saving} />
                  <Button
                    text="Save"
                    icon="check"
                    onPress={handleSave}
                    style={[
                      styles.headerButton,
                      { backgroundColor: colors.primary, opacity: canSave && !saving ? 1 : 0.5 },
                    ]}
                    textStyle={{ color: "white" }}
                    disabled={!canSave || saving}
                  />
                </>
              ) : (
                canEdit && (
                  <Pressable
                    onPress={() => setLocalEditing(true)}
                    style={[styles.editButton, { backgroundColor: colors.backgroundSecondary }]}
                  >
                    <MaterialCommunityIcons name="pencil" size={16} color={colors.primary} />
                    <ThemedText style={{ fontSize: 12, color: colors.primary, fontWeight: "500" }}>Edit</ThemedText>
                  </Pressable>
                )
              )}
            </View>
          </View>

          <View style={styles.editForm}>
            <View style={styles.ssnRow}>
              <View style={{ flex: 1 }}>
                <SSNInput
                  label="Social Security Number"
                  value={ssn}
                  onChangeText={setSsn}
                  editable={localEditing && canEdit}
                  loading={loading}
                />
              </View>
              {localEditing && privatePermissions.delete && ssn && (
                <Pressable
                  onPress={handleDelete}
                  style={[styles.deleteButton, { backgroundColor: colors.attention.background }]}
                >
                  <MaterialCommunityIcons name="trash-can-outline" size={18} color={colors.attention.text} />
                </Pressable>
              )}
            </View>
          </View>
        </View>

        {!canEdit && (
          <View style={[styles.permissionNotice, { backgroundColor: colors.attention.background }]}>
            <MaterialCommunityIcons name="information" size={16} color={colors.attention.text} />
            <ThemedText style={{ fontSize: 12, color: colors.attention.text, flex: 1 }}>
              You need &quot;user_private create&quot; or &quot;user_private update&quot; permission to modify private
              information.
            </ThemedText>
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flex: 1,
  },
  scrollContainer: {
    padding: 16,
    gap: 12,
  },
  section: {
    padding: 16,
    gap: 16,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    overflow: "hidden",
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  headerButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    minWidth: 70,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  editForm: {
    padding: 16,
    gap: 12,
  },
  ssnRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-end",
  },
  deleteButton: {
    padding: 10,
    borderRadius: 8,
    alignSelf: "flex-end",
    marginBottom: 6,
  },
  permissionNotice: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 8,
  },
});
